import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, UserCircle2, Check, AtSign, CalendarClock, ExternalLink } from "lucide-react";
import { openCalendlyPopup } from "@/lib/calendly";
import EmbedCodeCard from "@/components/EmbedCodeCard";
import { toast } from "sonner";

const roleLabelKey = {
  super_admin: "role.super_admin",
  institute_admin: "role.institute_admin",
  coach: "role.coach",
  user: "role.user",
};

export default function Account() {
  const { user, setUser } = useAuth();
  const { t } = useT();
  const [form, setForm] = useState({
    current_password: "",
    new_password: "",
    confirm: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Email-change form (separate state so password & email submits are independent)
  const [emailForm, setEmailForm] = useState({ new_email: "", current_password: "" });
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Calendly embed code (coach-only)
  const [calendlyInput, setCalendlyInput] = useState(user?.calendly_embed_code || "");
  const [calendlySubmitting, setCalendlySubmitting] = useState(false);
  const [calendlyError, setCalendlyError] = useState("");

  const saveCalendly = async (e) => {
    e.preventDefault();
    setCalendlyError("");
    setCalendlySubmitting(true);
    try {
      const { data } = await api.put("/auth/me/calendly", {
        calendly_embed_code: calendlyInput,
      });
      setUser(data);
      setCalendlyInput(data?.calendly_embed_code || "");
      toast.success(
        data?.calendly_embed_code
          ? "Your Calendly embed code has been saved."
          : "Calendly embed cleared — clients now see the platform default."
      );
    } catch (err) {
      setCalendlyError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save your Calendly embed code."
      );
    } finally {
      setCalendlySubmitting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.new_password.length < 6) {
      setError("New password must be at least 6 characters.");
      return;
    }
    if (form.new_password !== form.confirm) {
      setError("New password and confirmation don't match.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/me/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change password.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitEmail = async (e) => {
    e.preventDefault();
    setEmailError("");
    const newEmail = emailForm.new_email.trim().toLowerCase();
    if (!newEmail) {
      setEmailError("Enter your new email address.");
      return;
    }
    if (newEmail === (user?.email || "").toLowerCase()) {
      setEmailError("That's already your current email.");
      return;
    }
    setEmailSubmitting(true);
    try {
      const { data } = await api.post("/auth/me/change-email", {
        current_password: emailForm.current_password,
        new_email: newEmail,
      });
      setUser(data);
      toast.success("Email updated");
      setEmailForm({ new_email: "", current_password: "" });
    } catch (err) {
      setEmailError(formatApiErrorDetail(err.response?.data?.detail) || "Could not change email.");
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <div className="px-8 lg:px-12 py-12 max-w-3xl" data-testid="account-page">
      <PageHeader
        overline={t(roleLabelKey[user?.role] || "role.user")}
        title={t("account.title")}
        description={t("account.description")}
      />

      {/* Profile card */}
      <div
        className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mb-8"
        data-testid="account-profile-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
            <UserCircle2 strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
            {t("account.profile")}
          </h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Detail label={t("account.name")} value={user?.name} />
          <Detail label={t("account.email")} value={user?.email} />
          <Detail label={t("account.role")} value={t(roleLabelKey[user?.role] || "role.user")} />
        </div>
      </div>

      {/* Embed code card — coach only. Generates iframe/button/link snippets
          that deep-link straight to /register step 3 (details), with this
          coach pre-selected. */}
      {user?.role === "coach" && user?.institute_id && (
        <div className="mb-8" data-testid="embed-card-coach-wrap">
          <EmbedCodeCard
            variant="coach"
            registerUrl={`${typeof window !== "undefined" ? window.location.origin : ""}/register?institute=${encodeURIComponent(user.institute_id)}&coach=${encodeURIComponent(user.id)}`}
          />
        </div>
      )}

      {/* Calendly card — coach only */}
      {user?.role === "coach" && (
        <form
          onSubmit={saveCalendly}
          className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mb-8"
          data-testid="calendly-card"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
              <CalendarClock strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
            </div>
            <div>
              <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
                {t("account.calendly.title")}
              </h3>
              <p className="text-sm text-[#5C635A] mt-0.5 max-w-xl leading-relaxed">
                {t("account.calendly.subtitle")}
              </p>
            </div>
          </div>

          <div className="space-y-5 max-w-2xl">
            <div className="space-y-2">
              <Label
                htmlFor="calendly-input"
                className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]"
              >
                {t("account.calendly.label")}
              </Label>
              <textarea
                id="calendly-input"
                value={calendlyInput}
                onChange={(e) => setCalendlyInput(e.target.value)}
                rows={6}
                placeholder='<link href="https://assets.calendly.com/assets/external/widget.css" rel="stylesheet"><script src="https://assets.calendly.com/assets/external/widget.js" type="text/javascript" async></script><script>window.onload = function() { Calendly.initBadgeWidget({ url: "https://calendly.com/your-handle/event-name", text: "Schedule time with me", color: "#0069ff", textColor: "#ffffff", branding: true }); }</script>'
                data-testid="calendly-input"
                className="w-full rounded-lg bg-white border border-[#E5E2DC] px-3 py-2.5 text-xs font-mono leading-relaxed focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
              />
              <p className="text-[11px] text-[#8A9188] leading-relaxed">
                {t("account.calendly.tip_prefix")}<span className="font-mono">…/your-handle/<strong>event-name</strong></span>{t("account.calendly.tip_suffix")}
              </p>
            </div>

            {user?.calendly_url && (
              <div
                className="flex flex-wrap items-center gap-3 text-xs text-[#5C635A]"
                data-testid="calendly-current"
              >
                <span className="font-bold uppercase tracking-[0.2em] text-[#8A9188]">
                  {t("account.calendly.detected")}
                </span>
                <a
                  href={user.calendly_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-[#1A1D1A] hover:underline break-all"
                  data-testid="calendly-current-link"
                >
                  {user.calendly_url}
                </a>
                <button
                  type="button"
                  onClick={() =>
                    openCalendlyPopup(user.calendly_embed_code || user.calendly_url)
                  }
                  data-testid="calendly-preview-button"
                  className="inline-flex items-center gap-1 text-[#4A5D4E] hover:underline font-semibold"
                >
                  <ExternalLink strokeWidth={1.5} size={12} />
                  {t("account.calendly.preview")}
                </button>
              </div>
            )}

            {calendlyError && (
              <p
                className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
                data-testid="calendly-error"
              >
                {calendlyError}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-3">
              <Button
                type="submit"
                disabled={
                  calendlySubmitting ||
                  (calendlyInput || "").trim() === (user?.calendly_embed_code || "").trim()
                }
                data-testid="calendly-save-button"
                className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
              >
                <Check strokeWidth={1.5} size={16} className="mr-2" />
                {calendlySubmitting ? "Saving…" : "Save embed code"}
              </Button>
              {user?.calendly_embed_code && (
                <button
                  type="button"
                  onClick={() => {
                    setCalendlyInput("");
                  }}
                  data-testid="calendly-clear-button"
                  className="text-xs uppercase tracking-[0.2em] text-[#8A9188] hover:text-[#1A1D1A] font-bold"
                >
                  Clear embed code
                </button>
              )}
            </div>
          </div>
        </form>
      )}

      {/* Change-email card */}
      <form
        onSubmit={submitEmail}
        className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8 mb-8"
        data-testid="change-email-card"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
            <AtSign strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
              {t("account.change_email.title")}
            </h3>
            <p className="text-sm text-[#5C635A] mt-0.5">
              {t("account.change_email.subtitle")}
            </p>
          </div>
        </div>

        <div className="space-y-5 max-w-md">
          <FormField
            label={t("account.change_email.new")}
            type="email"
            value={emailForm.new_email}
            onChange={(v) => setEmailForm({ ...emailForm, new_email: v })}
            testid="account-new-email-input"
            autoComplete="email"
          />
          <FormField
            label={t("account.change_email.current_password")}
            type="password"
            value={emailForm.current_password}
            onChange={(v) => setEmailForm({ ...emailForm, current_password: v })}
            testid="account-email-current-password-input"
            autoComplete="current-password"
          />

          {emailError && (
            <p
              className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
              data-testid="account-email-error"
            >
              {emailError}
            </p>
          )}

          <Button
            type="submit"
            disabled={emailSubmitting || !emailForm.new_email || !emailForm.current_password}
            data-testid="account-change-email-submit"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            <Check strokeWidth={1.5} size={16} className="mr-2" />
            {emailSubmitting ? t("account.change_email.saving") : t("account.change_email.submit")}
          </Button>
        </div>
      </form>

      {/* Change-password card */}
      <form
        onSubmit={submit}
        className="bg-white border border-[#E5E2DC] rounded-2xl p-6 sm:p-8"
        data-testid="change-password-card"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-10 w-10 rounded-xl bg-[#F2F0EA] flex items-center justify-center">
            <KeyRound strokeWidth={1.5} className="text-[#B37A5F]" size={20} />
          </div>
          <div>
            <h3 className="font-heading text-xl text-[#1A1D1A] tracking-tight font-semibold">
              {t("account.change_password.title")}
            </h3>
            <p className="text-sm text-[#5C635A] mt-0.5">
              {t("account.change_password.subtitle")}
            </p>
          </div>
        </div>

        <div className="space-y-5 max-w-md">
          <FormField
            label={t("account.change_password.current")}
            type="password"
            value={form.current_password}
            onChange={(v) => setForm({ ...form, current_password: v })}
            testid="account-current-password-input"
            autoComplete="current-password"
          />
          <FormField
            label={t("account.change_password.new")}
            type="password"
            value={form.new_password}
            onChange={(v) => setForm({ ...form, new_password: v })}
            testid="account-new-password-input"
            autoComplete="new-password"
          />
          <FormField
            label={t("account.change_password.confirm")}
            type="password"
            value={form.confirm}
            onChange={(v) => setForm({ ...form, confirm: v })}
            testid="account-confirm-password-input"
            autoComplete="new-password"
          />

          {error && (
            <p
              className="text-sm text-[#9C6A53] bg-[#9C6A53]/10 border border-[#9C6A53]/20 rounded-lg px-4 py-3"
              data-testid="account-error"
            >
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={submitting || !form.current_password || !form.new_password || !form.confirm}
            data-testid="account-change-password-submit"
            className="h-11 rounded-lg bg-[#4A5D4E] hover:bg-[#3D4D41] text-white"
          >
            <Check strokeWidth={1.5} size={16} className="mr-2" />
            {submitting ? t("common.saving") : t("account.change_password.submit")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#8A9188] font-bold">{label}</p>
      <p className="text-[#1A1D1A] font-medium mt-1 truncate">{value || "—"}</p>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", testid, autoComplete }) {
  return (
    <div className="space-y-2">
      <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#1A1D1A]">{label}</Label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        autoComplete={autoComplete}
        className="h-11 rounded-lg bg-white border-[#E5E2DC] focus-visible:ring-1 focus-visible:ring-[#4A5D4E] focus-visible:border-[#4A5D4E]"
      />
    </div>
  );
}
